import React, { useState, useRef, useMemo, useCallback, useEffect } from "react";
import { useHistory } from "react-router-dom";
import moment from "moment";
import { AgGridReact } from "ag-grid-react";
import { FiEdit2 } from "react-icons/fi";
import { AiOutlineEye } from "react-icons/ai";
import { MdDeleteForever } from "react-icons/md";
import { BsFileEarmarkPdf } from "react-icons/bs";
import { toast } from "react-toastify";
import ActionButton from "../../../Common/ActionButton";
import SearchFilter from "../../../Common/SearchFilter";
import SelectFilter from "../../../Common/SelectFilter";
import DateRangeFilter from "../../../../Common/DateFilter";
import ColumnVisibilityControl from "../../../Common/ColumnVisibilityControl";
import StatusChip, { statusChipType } from "../../../Common/StatusChip";
import { getInvoicesApi, deleteInvoiceApi } from "../../../../Api/ClientWork/ClientWork";
import InvoiceDetailModal from "./InvoiceDetailModal";

/**
 * InvoicesTable Screen (Client Running Bills / Invoices / IPCs)
 * Inspired by GeneralReceiptTable and ERP voucher table registers.
 */
const InvoicesTable = () => {
  const history = useHistory();
  const gridRef = useRef(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dateFilter, setDateFilter] = useState({ from: "", to: "" });
  const [columnVisibility, setColumnVisibility] = useState({});
  const [invoices, setInvoices] = useState([]);
  const [detailInvoice, setDetailInvoice] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const loadData = useCallback(async () => {
    const result = await getInvoicesApi();
    if (result?.data?.IsSuccess === true) {
      setInvoices(result.data.Data || []);
    } else {
      toast.error(result?.data?.Message || "Could not load running bills");
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDelete = useCallback(async (id, billNo) => {
    if (
      window.confirm(`Are you sure you want to delete Running Bill "${billNo || "this bill"}"?`)
    ) {
      const result = await deleteInvoiceApi(id);
      if (result?.data?.IsSuccess === true) {
        setInvoices((prev) => prev.filter((b) => b.Id !== id));
        toast.success("Running Bill deleted successfully");
      } else {
        toast.error(result?.data?.Message || "Could not delete Running Bill");
      }
    }
  }, []);

  // Filtered dataset based on search, status, and date range
  const filteredInvoices = useMemo(() => {
    return invoices.filter((item) => {
      // Status Filter
      if (statusFilter && item.Status !== statusFilter) {
        return false;
      }
      // Date Range Filter
      if (dateFilter.from && dateFilter.to && item.BillingDate) {
        const billMoment = moment(item.BillingDate);
        const fromMoment = moment(dateFilter.from);
        const toMoment = moment(dateFilter.to);
        if (billMoment.isBefore(fromMoment, "day") || billMoment.isAfter(toMoment, "day")) {
          return false;
        }
      }
      return true;
    });
  }, [invoices, statusFilter, dateFilter]);

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Sr",
        colId: "sr",
        valueGetter: "node.rowIndex + 1",
        width: 60,
        minWidth: 60,
        maxWidth: 70,
        suppressSizeToFit: true,
        filter: false,
        sortable: false,
        cellStyle: { textAlign: "center" },
      },
      {
        headerName: "Bill / Invoice No",
        field: "BillNumber",
        flex: 1.3,
        minWidth: 150,
        cellRenderer: (params) => (
          <span
            style={{
              fontWeight: 600,
              color: "var(--color-primary, #0b6462)",
              cursor: "pointer",
            }}
            onClick={(event) => {
              event.stopPropagation();
              setDetailInvoice(params.data);
              setShowDetailModal(true);
            }}
          >
            {params.value || "—"}
          </span>
        ),
      },
      {
        headerName: "Billing Date",
        field: "BillingDate",
        flex: 1,
        minWidth: 120,
        valueFormatter: (params) =>
          params.value ? moment(params.value).format("DD-MMM-YYYY") : "—",
      },
      {
        headerName: "Contract No",
        field: "ContractNo",
        flex: 1.2,
        minWidth: 140,
      },
      {
        headerName: "Project Owner (Client)",
        field: "ClientName",
        flex: 1.8,
        minWidth: 200,
        tooltipValueGetter: (p) =>
          p.data?.ClientNTN ? `${p.value || ""} (NTN: ${p.data.ClientNTN})` : p.value,
      },
      {
        headerName: "Project Name",
        field: "ProjectName",
        flex: 1.4,
        minWidth: 150,
      },
      {
        headerName: "Progress Period",
        field: "PeriodFrom",
        flex: 1.6,
        minWidth: 170,
        valueFormatter: (params) => {
          if (!params.data?.PeriodFrom && !params.data?.PeriodTo) {
            return "—";
          }
          const fromStr = params.data?.PeriodFrom
            ? moment(params.data.PeriodFrom).format("DD-MMM")
            : "";
          const toStr = params.data?.PeriodTo
            ? moment(params.data.PeriodTo).format("DD-MMM-YYYY")
            : "";
          return `${fromStr} to ${toStr}`;
        },
      },
      {
        headerName: "Current Gross",
        field: "CurrentGross",
        flex: 1.3,
        minWidth: 130,
        valueFormatter: (params) =>
          params.value ? `PKR ${Number(params.value).toLocaleString()}` : "—",
      },
      {
        headerName: "Deductions",
        field: "TotalDeductions",
        flex: 1.2,
        minWidth: 120,
        valueFormatter: (params) =>
          params.value ? `PKR ${Number(params.value).toLocaleString()}` : "—",
      },
      {
        headerName: "Net Receivable",
        field: "NetReceivable",
        flex: 1.4,
        minWidth: 140,
        valueFormatter: (params) =>
          params.value ? `PKR ${Number(params.value).toLocaleString()}` : "—",
      },
      {
        headerName: "Status",
        field: "Status",
        width: 120,
        cellRenderer: (params) => {
          const val = params.value || "Draft";
          return <StatusChip status={val} type={statusChipType(val)} />;
        },
      },
      {
        headerName: "Action",
        field: "action",
        width: 120,
        pinned: "right",
        sortable: false,
        filter: false,
        headerComponent: () => (
          <div className="d-flex justify-content-center w-100">
            <ActionButton
              text="New+"
              newBtn="true"
              onClick={() => history.push("/client-work/invoices/add")}
              variant="save"
            />
          </div>
        ),
        cellRenderer: (params) => (
          <div
            className="d-flex align-items-center justify-content-center"
            style={{ gap: "10px", height: "100%" }}
          >
            {params.data?.ProgressCertificateFile && (
              <BsFileEarmarkPdf
                title={`Attachment: ${params.data.ProgressCertificateFile}`}
                size="1.2em"
                style={{ cursor: "pointer", color: "#e11d48" }}
                onClick={(e) => {
                  e.stopPropagation();
                  toast.info(`Viewing certificate "${params.data.ProgressCertificateFile}"`);
                }}
              />
            )}
            <AiOutlineEye
              title="View Invoice Details"
              size="1.25em"
              style={{ cursor: "pointer", color: "#075e5c" }}
              onClick={(e) => {
                e.stopPropagation();
                setDetailInvoice(params.data);
                setShowDetailModal(true);
              }}
            />
            <FiEdit2
              title="Edit Bill"
              size="1.15em"
              style={{ cursor: "pointer", color: "#4ac8d1" }}
              onClick={(e) => {
                e.stopPropagation();
                history.push(`/client-work/invoices/edit/${params.data?.Id}`);
              }}
            />
            <MdDeleteForever
              title="Delete Bill"
              size="1.3em"
              style={{ cursor: "pointer", color: "#dc3545" }}
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(params.data?.Id, params.data?.BillNumber);
              }}
            />
          </div>
        ),
      },
    ],
    [history, handleDelete],
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      tooltipValueGetter: (p) => p.value,
      valueFormatter: (p) =>
        p.value === null || p.value === undefined || p.value === "" ? "—" : p.value,
      minWidth: 100,
    }),
    [],
  );

  const onGridReady = useCallback((params) => {
    const map = {};
    params.api.getColumns()?.forEach((col) => {
      if (col.getColDef().headerName) {
        map[col.getColId()] = col.isVisible();
      }
    });
    setColumnVisibility(map);
  }, []);

  const toggleColumn = useCallback(
    (colId) => {
      if (!gridRef.current?.api) {
        return;
      }
      const isCurrentlyVisible = columnVisibility[colId];
      gridRef.current.api.setColumnsVisible([colId], !isCurrentlyVisible);
      setColumnVisibility((prev) => ({ ...prev, [colId]: !isCurrentlyVisible }));
    },
    [columnVisibility],
  );

  return (
    <div className="pt-2">
      <InvoiceDetailModal
        isOpen={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        invoiceData={detailInvoice}
      />
      {/* ── Filter Toolbar ── */}
      <div
        className="d-flex flex-wrap align-items-center justify-content-end pt-3 app_font w-100"
        style={{ gap: "12px", width: "100%" }}
      >
        <div
          className="d-flex align-items-center"
          style={{
            minHeight: "30px",
            maxWidth: "100%",
            marginRight: "auto",
            padding: "4px 12px",
            color: "var(--btn-comp-text, #0b6462)",
            fontSize: "var(--font-md)",
            fontWeight: 600,
            whiteSpace: "nowrap",
          }}
          title="Invoices"
        >
          Invoices
        </div>
        {/* Search Filter */}
        <SearchFilter
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search Invoices by Bill No, Client, Contract..."
        />

        {/* Floating Date Range Filter */}
        <div className="d-flex align-items-center" style={{ maxWidth: "250px", flexGrow: 1 }}>
          <div className="floating-field mt-1" style={{ width: "100%" }}>
            <DateRangeFilter
              onChange={(val) => {
                setDateFilter(val);
              }}
            />
            <label>Date</label>
          </div>
        </div>

        {/* Status Filter */}
        <SelectFilter
          id="statusFilter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          label="Status"
          defaultOptionLabel="All"
          maxWidth="200px"
          options={[
            { value: "Draft", label: "Draft" },
            { value: "Authorized", label: "Authorized / Issued" },
            { value: "Partially Received", label: "Partially Received" },
            { value: "Paid", label: "Paid" },
            { value: "Cancelled", label: "Cancelled" },
          ]}
        />

        {/* Column Visibility Control */}
        <ColumnVisibilityControl
          gridApi={gridRef.current?.api}
          columnVisibility={columnVisibility}
          toggleColumn={toggleColumn}
        />
      </div>

      {/* ── Table Grid ── */}
      <div className="row">
        <div className="col-12 pr-0 pl-0">
          <div className="ag-theme-alpine vouchers_table_height2 mt-3" style={{ width: "100%" }}>
            <AgGridReact
              ref={gridRef}
              rowData={filteredInvoices}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              quickFilterText={searchTerm}
              pagination={true}
              paginationPageSize={15}
              tooltipShowDelay={0}
              onGridReady={onGridReady}
              rowStyle={{ cursor: "pointer" }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoicesTable;
